import React, { useState } from 'react';
import { Table, Button, Modal, Form } from 'react-bootstrap';
import { toDollarAmount } from '../../../app-utils';
import { capitalize } from '@mui/material';
import { Payment } from '../../../../../entities/hydra';
import { floatVal } from '@jamesgmarks/utilities';
import { updateOne } from '../../../redux/features/dynamic-hydra/actions';
import { useAppSelector } from "src/redux/hooks";
import { EHydraPaymentState } from "src/redux/features/payments/interfaces";
import { RequirePermission } from '../../parts/RequirePermission';

export const PaymentHistoryTab = (
  { payments, refreshPayments }: { payments: Payment[], refreshPayments: () => void },
) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<string>('');
  const userId = useAppSelector(state => state.auth?.decodedToken?.userId);

  const totalAmount = payments.reduce((sum, payment) => sum + floatVal(payment.amount), 0);

  const handleUpdateState = async () => {
    if (selectedPaymentId !== null) {
      const success = await updateOne("payments", JSON.stringify({
        id: selectedPaymentId,
        hydraState: selectedState,
        modifiedUserId: userId,
      }), selectedPaymentId);
      if (success) {
        refreshPayments();
      }
      setShowModal(false);
      setSelectedPaymentId(null);
      setSelectedState('');
    }
  };

  const handleOpenModal = (paymentId: number, currentState: string) => {
    setSelectedPaymentId(paymentId);
    setSelectedState(currentState);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPaymentId(null);
    setSelectedState('');
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>Payment ID</th>
            <th>Payment Date</th>
            <th>Payment Type</th>
            <th>Payment Source</th>
            <th>Status</th>
            <th>Amount</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments?.map((payment) => (
            <tr key={payment.id}>
              <td>{payment.id}</td>
              <td>{payment.datePaid}</td>
              <td>{capitalize(payment.paymentType)}</td>
              <td>{capitalize(payment.sourceType)}</td>
              <td>{capitalize(payment.hydraState)}</td>
              <td>{toDollarAmount(floatVal(payment.amount) || 0)}</td>
              <td>
                <RequirePermission grant="EDIT_PAYMENT">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleOpenModal(payment.id, payment.hydraState)}
                  >
                    Edit State
                  </Button>
                </RequirePermission>
              </td>
            </tr>
          ))}
          <tr>
            <td colSpan={6} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
            <td>{toDollarAmount(totalAmount)}</td>
          </tr>
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Payment State</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="paymentState">
              <Form.Label>Payment State</Form.Label>
              <Form.Control
                as="select"
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                {Object.values(EHydraPaymentState).map((state) => (
                  <option key={state} value={state}>
                    {capitalize(state)}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleUpdateState}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
